<script setup>
import { $api, getApiBaseUrl } from "@/utils/api";

const route = useRoute();
const router = useRouter();
const id = computed(() => route.params.id);
const isEdit = computed(
  () => route.query.edit === "1" || route.query.edit === "true"
);

definePage({
  meta: {
    requiresAuth: true,
  },
});

const contrato = ref(null);
const loading = ref(true);
const error = ref(false);

async function fetchContrato() {
  loading.value = true;
  error.value = false;
  try {
    contrato.value = await $api(`/contratos-prestadores/${id.value}`);
    // Normalizar datas string -> Date para o form
    const d = contrato.value;
    const dateFields = [
      "data_nascimento",
      "data_admissao",
      "data_proposta",
      "data_inicio_servico",
    ];
    dateFields.forEach((f) => {
      const val = d[f];
      if (val == null || val === "") return;
      if (typeof val === "string") {
        const s = val.trim();
        if (!s) return;
        d[f] = s.includes("T")
          ? new Date(s)
          : new Date(`${s.slice(0, 10)}T12:00:00`);
      }
    });
  } catch (e) {
    console.error(e);
    error.value = true;
    contrato.value = null;
  } finally {
    loading.value = false;
  }
}

function onSaved() {
  router.push({ name: "contratos" });
}

function onCancel() {
  router.replace({ name: "contratos-id", params: { id: id.value }, query: {} });
}

function goEdit() {
  router.replace({
    name: "contratos-id",
    params: { id: id.value },
    query: { edit: "1" },
  });
}

const pdfError = ref("");
const pdfSnackbar = ref(false);
const anexoError = ref("");
const anexoSnackbar = ref(false);

const temAnexo = computed(() => contrato.value?.anexo_path || contrato.value?.origem === "anexo");

async function verAnexo() {
  anexoError.value = "";
  anexoSnackbar.value = false;
  try {
    const token = useCookie("accessToken").value;
    if (!token) {
      anexoError.value = "Faça login novamente para abrir o anexo.";
      anexoSnackbar.value = true;
      return;
    }
    const apiBase = getApiBaseUrl();
    const urlAnexo = `${apiBase}/contratos-prestadores/${id.value}/anexo`;
    const res = await fetch(urlAnexo, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    const contentType = res.headers.get("content-type") || "";
    if (!res.ok) {
      let msg = "Falha ao abrir anexo.";
      if (contentType.includes("application/json")) {
        try {
          const data = await res.json();
          msg = data?.message || msg;
          if (data?.error) msg += " (" + data.error + ")";
        } catch (_) {}
      } else {
        const text = await res.text();
        if (text) msg = `Erro ${res.status}: ${text.slice(0, 100)}`;
      }
      anexoError.value = msg;
      anexoSnackbar.value = true;
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  } catch (e) {
    console.error(e);
    anexoError.value = e?.message || "Erro de rede ao abrir anexo. Tente novamente.";
    anexoSnackbar.value = true;
  }
}

async function abrirPdf() {
  pdfError.value = "";
  pdfSnackbar.value = false;
  try {
    const token = useCookie("accessToken").value;
    const apiBase = getApiBaseUrl();
    const res = await fetch(
      `${apiBase}/contratos-prestadores/${id.value}/pdf`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    const contentType = res.headers.get("content-type") || "";
    if (!res.ok) {
      let msg = "Falha ao gerar PDF.";
      if (contentType.includes("application/json")) {
        try {
          const data = await res.json();
          msg = data?.error || data?.message || msg;
        } catch (_) {}
      }
      pdfError.value = msg;
      pdfSnackbar.value = true;
      return;
    }
    if (contentType.includes("application/pdf")) {
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } else {
      pdfError.value = "Resposta inválida do servidor.";
      pdfSnackbar.value = true;
    }
  } catch (e) {
    console.error(e);
    pdfError.value = e?.message || "Erro ao gerar PDF. Verifique a conexão.";
    pdfSnackbar.value = true;
  }
}

onMounted(() => fetchContrato());
</script>

<template>
  <div>
    <VCard v-if="loading">
      <VCardText class="text-center py-8">
        <VProgressCircular indeterminate color="primary" />
        <p class="mt-2">Carregando...</p>
      </VCardText>
    </VCard>
    <VCard v-else-if="error">
      <VCardText class="text-center py-8">
        <p class="text-error">Contrato não encontrado.</p>
        <VBtn class="mt-2" :to="{ name: 'contratos' }"> Voltar </VBtn>
      </VCardText>
    </VCard>
    <VCard v-else>
      <VCardTitle class="d-flex align-center flex-wrap gap-2">
        <VBtn icon variant="text" class="me-2" :to="{ name: 'contratos' }">
          <VIcon icon="tabler-arrow-left" />
        </VBtn>
        {{ isEdit ? "Editar" : "Visualizar" }} Contrato —
        {{ contrato?.numero_contrato }}
        <VSpacer />
        <template v-if="!isEdit">
          <VBtn variant="outlined" @click="goEdit"> Editar </VBtn>
          <VBtn
            v-if="temAnexo"
            variant="outlined"
            color="secondary"
            @click="verAnexo"
          >
            <VIcon icon="tabler-paperclip" start />
            Ver anexo
          </VBtn>
          <VBtn color="primary" @click="abrirPdf"> Gerar PDF </VBtn>
        </template>
      </VCardTitle>
      <VDivider />
      <VCardText>
        <FormContratoPrestador
          v-if="contrato"
          v-model="contrato"
          :readonly="!isEdit"
          @saved="onSaved"
          @cancel="onCancel" />
      </VCardText>
    </VCard>

    <VSnackbar
      v-model="pdfSnackbar"
      color="error"
      location="top"
      :timeout="8000">
      {{ pdfError }}
      <template #actions>
        <VBtn variant="text" size="small" @click="pdfSnackbar = false">
          Fechar
        </VBtn>
      </template>
    </VSnackbar>
    <VSnackbar
      v-model="anexoSnackbar"
      color="error"
      location="top"
      :timeout="8000">
      {{ anexoError }}
      <template #actions>
        <VBtn variant="text" size="small" @click="anexoSnackbar = false">
          Fechar
        </VBtn>
      </template>
    </VSnackbar>
  </div>
</template>
